import { useEffect, useRef, useState } from 'react';
import I18n from 'i18n-js';
import useLiveSession from '@/channels/liveSession/useLiveSession';
import useStudentLiveSession from '@/channels/studentLiveSession/useStudentLiveSession';
import tpmToast, { dismissTpmToast } from '@/components/admin/SlideShow/PresentMode/tpmToast';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import { logLiveSessionEvent } from '@/modules/liveSessionEvents';
import type { TeacherConnectionPopupVariant } from '@/components/student/TeacherConnectionPopup';

interface UseStudentSlideSyncArgs {
  activeSessionId: string | null;
  forceJumpToSlide: (slideId: number, slideType: string) => void;
  slideShowId: number | string;
  userType: string;
}

interface UseStudentSlideSyncReturn {
  closeConnectionPopup: () => void;
  connectionPopup: TeacherConnectionPopupVariant | null;
}

const DISCONNECT_DEBOUNCE_MS = 5000;
const CONNECTION_TOAST_ID = 'tpm-connection-lost';

// AIDEV-NOTE: Bridges the channel stores into the SlideShowContext that drives lock/popup
// state across PresentMode subcomponents. PLAT-16777 — disconnect popup is debounced;
// reconnect either cancels the pending timer (silent recovery) or fires the reconnected
// variant when the timer already elapsed.
const useStudentSlideSync = ({
  activeSessionId,
  forceJumpToSlide,
  slideShowId,
  userType,
}: UseStudentSlideSyncArgs): UseStudentSlideSyncReturn => {
  const isStudent = userType === 'Student';

  const setActiveSessionId = useSlideShowContext(state => state.setActiveSessionId);
  const setDisconnectedSessionId = useSlideShowContext(state => state.setDisconnectedSessionId);
  const setIsSessionConnected = useSlideShowContext(state => state.setIsSessionConnected);

  const {
    joinNotification,
    removalNotification,
  } = useStudentLiveSession({ enabled: isStudent });

  // Filter by slide_show_id — a join for slideshow B shouldn't lock a student viewing A.
  useEffect(() => {
    if (!isStudent) return;
    if (!joinNotification?.session_id) return;
    if (Number(joinNotification.slide_show_id) !== Number(slideShowId)) {
      logLiveSessionEvent(joinNotification.session_id, 'join_notification_suppressed', {
        current_slide_show_id: Number(slideShowId),
        notified_slide_show_id: Number(joinNotification.slide_show_id),
        reason: 'other_slide_show',
      });
      return;
    }
    setActiveSessionId(joinNotification.session_id);
  }, [isStudent, joinNotification, slideShowId, setActiveSessionId]);

  useEffect(() => {
    if (!isStudent) return;
    if (!removalNotification) return;
    setActiveSessionId(null);
  }, [isStudent, removalNotification, setActiveSessionId]);

  const {
    connectionState,
    currentSlide,
    markAbsent,
    markPresent,
    sessionEndedReason,
    teacherDisconnectedAt,
    teacherReconnectedAt,
  } = useLiveSession(isStudent ? activeSessionId : null);

  // Synchronous mirror — back-to-back navigate/disconnect/reconnect events outrun React
  // re-renders, so we need a ref to suppress stale navigates after the timer settles.
  const disconnectedSessionIdRef = useRef<string | null>(null);
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // PLAT-17326 — suppresses the connection-lost toast during a real page-unload, where the
  // WS `disconnected` fires as a side effect of teardown rather than a real drop.
  const isUnloadingRef = useRef(false);
  const [connectionPopup, setConnectionPopup] = useState<TeacherConnectionPopupVariant | null>(null);

  // Ref-mirror forceJumpToSlide so the effects below don't re-attach on parent re-renders.
  const forceJumpRef = useRef(forceJumpToSlide);
  useEffect(() => {
    forceJumpRef.current = forceJumpToSlide;
  }, [forceJumpToSlide]);

  useEffect(() => {
    const onUnload = () => { isUnloadingRef.current = true; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  useEffect(() => {
    if (activeSessionId) return;
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    setConnectionPopup(null);
  }, [activeSessionId]);

  useEffect(() => () => {
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isStudent || !activeSessionId) return;
    if (connectionState === 'connected') {
      setIsSessionConnected(true);
      dismissTpmToast(CONNECTION_TOAST_ID);
    } else if (connectionState === 'disconnected') {
      if (isUnloadingRef.current) return;
      setIsSessionConnected(false);
      tpmToast(I18n.t('live_session_connection_lost_body'), {
        msgHeading: I18n.t('live_session_connection_lost_title'),
        msgType: 'warning',
        toastId: CONNECTION_TOAST_ID,
      });
    } else if (connectionState === 'rejected') {
      setActiveSessionId(null);
      setIsSessionConnected(null);
      dismissTpmToast(CONNECTION_TOAST_ID);
    }
  }, [isStudent, activeSessionId, connectionState, setActiveSessionId, setIsSessionConnected]);

  // PLAT-17441 — Presence is scoped to actually viewing the slideshow, not merely being
  // subscribed (the layout listener subscribes for lifecycle while showing only the popup).
  // Re-marks on reconnect when connectionState flips back to 'connected'; marks absent on
  // unmount/leave. The server's unsubscribed handler is the backstop for hard disconnects.
  useEffect(() => {
    if (!isStudent || !activeSessionId) return undefined;
    if (connectionState !== 'connected') return undefined;
    markPresent();
    return () => markAbsent();
  }, [isStudent, activeSessionId, connectionState, markPresent, markAbsent]);

  // Lifecycle teardown only — StudentSessionListener renders the ended popup off the shared store.
  useEffect(() => {
    if (!isStudent || !sessionEndedReason) return;
    setActiveSessionId(null);
    setIsSessionConnected(null);
    dismissTpmToast(CONNECTION_TOAST_ID);
  }, [
    isStudent,
    sessionEndedReason,
    setActiveSessionId,
    setIsSessionConnected,
  ]);

  // Suppress the snap while the disconnect ref points at this session — the debounce timer
  // already elapsed, so any in-flight nav from the now-gone teacher must not slip through.
  useEffect(() => {
    if (!isStudent || !currentSlide) return;
    if (disconnectedSessionIdRef.current === activeSessionId) {
      logLiveSessionEvent(activeSessionId, 'nav_suppressed', {
        reason: 'teacher_disconnected',
        slide_id: currentSlide.slideId,
        slide_type: currentSlide.slideType,
      });
      return;
    }
    forceJumpRef.current(currentSlide.slideId, currentSlide.slideType);
  }, [isStudent, activeSessionId, currentSlide]);

  useEffect(() => {
    if (!isStudent || !teacherDisconnectedAt) return;
    if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
    disconnectTimerRef.current = setTimeout(() => {
      disconnectTimerRef.current = null;
      disconnectedSessionIdRef.current = activeSessionId;
      setDisconnectedSessionId(activeSessionId);
      setConnectionPopup('disconnected');
      logLiveSessionEvent(activeSessionId, 'disconnect_debounce_expired', { debounce_ms: DISCONNECT_DEBOUNCE_MS });
    }, DISCONNECT_DEBOUNCE_MS);
  }, [isStudent, activeSessionId, teacherDisconnectedAt, setDisconnectedSessionId]);

  useEffect(() => {
    if (!isStudent || !teacherReconnectedAt) return;
    const wasDebounced = disconnectTimerRef.current !== null;
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    if (teacherReconnectedAt.slideId != null && teacherReconnectedAt.slideType) {
      forceJumpRef.current(teacherReconnectedAt.slideId, teacherReconnectedAt.slideType);
    }
    if (wasDebounced) {
      logLiveSessionEvent(activeSessionId, 'silent_reconnect', {
        slide_id: teacherReconnectedAt.slideId,
        slide_type: teacherReconnectedAt.slideType,
      });
      return;
    }
    disconnectedSessionIdRef.current = null;
    setDisconnectedSessionId(null);
    setConnectionPopup('reconnected');
  }, [isStudent, activeSessionId, teacherReconnectedAt, setDisconnectedSessionId]);

  return {
    closeConnectionPopup: () => setConnectionPopup(null),
    connectionPopup,
  };
};

export default useStudentSlideSync;
